package app

import kotlin.math.pow
import kotlin.random.Random

/**
 * Weighted random picker.
 *
 * Goal: choose a movie to watch, strongly favouring the one watched longest ago,
 * while giving recently watched movies a much weaker chance and staying
 * non-deterministic.
 *
 * Rules:
 *  - A hard [cooldownDays] window excludes movies watched too recently, so the
 *    same movie will not come up again right away. If every movie is inside the
 *    window, the whole list is used as a fallback.
 *  - Inside the eligible pool the weight grows with the days since the last
 *    watch: weight = (daysSince + 1) ^ strength. Higher [strength] biases harder
 *    towards old / never-watched movies.
 *  - The final choice is a weighted random draw, so the result is never a fixed
 *    order.
 */
object Picker {

    /** Days assigned to a never-watched movie so it ranks as very old. */
    const val UNWATCHED_DAYS: Long = 3650

    fun daysSince(movie: Movie, today: Long): Long =
        when (val d = movie.lastWatchedDay) {
            null -> UNWATCHED_DAYS
            else -> (today - d).coerceAtLeast(0)
        }

    fun weight(movie: Movie, today: Long, strength: Double): Double =
        (daysSince(movie, today).toDouble() + 1.0).pow(strength)

    /** Movies allowed to be drawn: watched before the cooldown window, or never. */
    fun eligible(movies: List<Movie>, today: Long, cooldownDays: Long): List<Movie> =
        movies.filter { daysSince(it, today) >= cooldownDays }

    /**
     * Pick one movie. Returns null only when [movies] is empty.
     */
    fun pick(
        movies: List<Movie>,
        today: Long,
        cooldownDays: Long,
        strength: Double,
        random: Random = Random.Default,
    ): Movie? {
        if (movies.isEmpty()) return null

        val pool = eligible(movies, today, cooldownDays).ifEmpty { movies }

        val weights = pool.map { weight(it, today, strength) }
        val total = weights.sum()
        if (total <= 0.0) return pool[random.nextInt(pool.size)]

        var threshold = random.nextDouble() * total
        for (i in pool.indices) {
            threshold -= weights[i]
            if (threshold <= 0.0) return pool[i]
        }
        return pool.last()
    }
}
